import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import LearnerShell from "./learnerShell";
import { resolveLmsShortName } from "./_learnerConfig";
import { getCachedPortalInfo, loadPortalInfoCached } from "./learnerPortalInfoCache";

const LearnerSupport = () => {
  const { t } = useTranslation();
  const { orgName } = useParams();
  const [organisation, setOrganisation] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);

  const lmsShortName = orgName || resolveLmsShortName();
  const cachedOrganisation = getCachedPortalInfo(lmsShortName)?.organisation || null;
  const visibleOrganisation = organisation || cachedOrganisation;

  useEffect(() => {
    let active = true;
    const loadOrganisation = async () => {
      if (!lmsShortName) {
        if (active) setLoadingOrg(false);
        return;
      }
      try {
        const data = await loadPortalInfoCached(lmsShortName);
        if (!active) return;
        setOrganisation(data?.organisation || null);
      } catch {
        if (active && !getCachedPortalInfo(lmsShortName)?.organisation) setOrganisation(null);
      }
      if (active) setLoadingOrg(false);
    };
    loadOrganisation();
    return () => {
      active = false;
    };
  }, [lmsShortName]);

  const supportConfig = useMemo(() => {
    const support = visibleOrganisation?.support || {};
    return {
      email: String(support.email || visibleOrganisation?.supportEmail || "").trim(),
      contactNumber: String(
        support.contactNumber || visibleOrganisation?.supportContactNumber || ""
      ).trim(),
      helpAndSupport: String(
        support.helpAndSupport || visibleOrganisation?.helpAndSupport || ""
      ).trim(),
    };
  }, [visibleOrganisation]);

  const helpAndSupportText = useMemo(() => {
    if (!supportConfig.helpAndSupport) return "";
    return String(supportConfig.helpAndSupport)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, [supportConfig.helpAndSupport]);

  const supportItems = useMemo(
    () => [
      {
        key: "email",
        icon: "fa-envelope",
        label: t("learner.lms.support.email", "Support email"),
        value: supportConfig.email,
        href: supportConfig.email ? "mailto:" + supportConfig.email : "",
      },
      {
        key: "contactNumber",
        icon: "fa-phone-alt",
        label: t("learner.lms.support.contactNumber", "Support contact number"),
        value: supportConfig.contactNumber,
        href: supportConfig.contactNumber
          ? "tel:" + supportConfig.contactNumber.replace(/\s+/g, "")
          : "",
      },
      {
        key: "guidance",
        icon: "fa-life-ring",
        label: t("learner.lms.support.guidance", "Help & support guidance"),
        value: helpAndSupportText,
        href: "",
      },
    ],
    [supportConfig, helpAndSupportText, t]
  );

  return (
    <LearnerShell title={t("learner.lms.nav.support", "Help & Support")} wide>
      <div className="support-container">
        <section className="support-center-card">
          <div className="support-center-logo">
            {visibleOrganisation?.brandLogoUrl ? (
              <img
                src={visibleOrganisation.brandLogoUrl}
                alt={visibleOrganisation?.name || t("learner.lms.support.organisationLogo", "Organisation logo")}
              />
            ) : (
              <i className="fas fa-life-ring"></i>
            )}
          </div>
          <span className="support-center-eyebrow">
            {visibleOrganisation?.name || t("learner.lms.support.learnerPortal", "Learner portal")}
          </span>
          <h1 className="support-center-title">{t("learner.lms.nav.support", "Help & Support")}</h1>
          <p className="support-center-intro">
            {loadingOrg
              ? t("learner.lms.support.loadingDetails", "Loading support details...")
              : t(
                  "learner.lms.support.intro",
                  "Use the details below to contact your organisation for learner support."
                )}
          </p>

          <div className="support-center-list">
            {supportItems.map((item) => (
              <div key={item.key} className="support-center-row">
                <span className="support-center-row-icon">
                  <i className={"fas " + item.icon}></i>
                </span>
                <div className="support-center-row-copy">
                  <span>{item.label}</span>
                  {item.value ? (
                    item.href ? (
                      <a href={item.href}>{item.value}</a>
                    ) : (
                      <strong>{item.value}</strong>
                    )
                  ) : (
                    <strong className="is-muted">
                      {loadingOrg
                        ? t("common.loading", "Loading...")
                        : t("learner.lms.support.notProvided", "Not provided")}
                    </strong>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </LearnerShell>
  );
};

export default LearnerSupport;
